import React, { useState, useEffect } from 'react';
import { Button, TextField, Box, Typography, IconButton, Switch, FormControlLabel, Stack, Chip } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import axios from 'axios';

const emptyAccount = {
  account_name: '', api_key: '', api_secret: '', user_id: '',
  password: '', totp_key: '', multiplier: '', active: false,
};

const ZerodhaPlaceAccount = () => {
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountNames, setAccountNames] = useState([]);
  const [accountDetails, setAccountDetails] = useState(emptyAccount);
  const [show, setShow] = useState({ api_key: false, api_secret: false, password: false, totp_key: false });
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchNames = async () => {
      const names = [];
      for (let i = 1; i <= 10; i++) {
        const account = `Zerodha${i}`;
        try {
          const r = await axios.get(`/api/get_zerodha_account/${account}`);
          names.push(r.data?.account_name || account);
        } catch { names.push(account); }
      }
      setAccountNames(names);
    };
    fetchNames();
  }, []);

  useEffect(() => {
    if (selectedAccount) fetchAccountDetails(selectedAccount);
  }, [selectedAccount]);

  const fetchAccountDetails = async (account) => {
    try {
      const r = await axios.get(`/api/get_zerodha_account/${account}`);
      setAccountDetails(Object.keys(r.data || {}).length > 0 ? { ...emptyAccount, ...r.data } : emptyAccount);
    } catch { setAccountDetails(emptyAccount); }
  };

  const handleSave = async () => {
    const { account_name, api_key, api_secret, user_id, password, totp_key, multiplier } = accountDetails;
    if (!account_name || !api_key || !api_secret || !user_id || !password || !totp_key || !multiplier) {
      setMessage('All fields are required'); return;
    }
    try {
      await axios.post(`/api/save_zerodha_account/${selectedAccount}`, accountDetails);
      setMessage('Account saved successfully!');
    } catch { setMessage('Error saving account.'); }
  };

  const handleInput = (field, value) => setAccountDetails(prev => ({ ...prev, [field]: value }));
  const toggleShow = (field) => setShow(prev => ({ ...prev, [field]: !prev[field] }));

  const Eye = ({ field }) => (
    <IconButton size="small" onClick={() => toggleShow(field)} edge="end">
      {show[field] ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
    </IconButton>
  );

  const accountIdx = selectedAccount ? parseInt(selectedAccount.replace('Zerodha', '')) - 1 : -1;

  return (
    <Box>
      {/* Account Selector */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Select Account</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {Array.from({ length: 10 }, (_, i) => {
            const acct = `Zerodha${i + 1}`;
            const isSelected = selectedAccount === acct;
            return (
              <Chip
                key={i}
                label={accountNames[i] || `Zerodha ${i + 1}`}
                onClick={() => setSelectedAccount(acct)}
                variant={isSelected ? 'filled' : 'outlined'}
                sx={{
                  bgcolor: isSelected ? 'primary.main' : 'transparent',
                  color: isSelected ? '#fff' : 'text.secondary',
                  borderColor: isSelected ? 'primary.main' : '#e2e8f0',
                  fontWeight: isSelected ? 600 : 400,
                  '&:hover': { bgcolor: isSelected ? 'primary.dark' : '#f8fafc' },
                  cursor: 'pointer',
                }}
              />
            );
          })}
        </Box>
      </Box>

      {selectedAccount && (
        <Box sx={{ maxWidth: 520 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            {accountNames[accountIdx] || selectedAccount}
          </Typography>
          <Stack spacing={1.5}>
            <TextField label="Account Name" value={accountDetails.account_name}
              onChange={e => handleInput('account_name', e.target.value)} fullWidth />
            <TextField label="API Key" value={accountDetails.api_key}
              onChange={e => handleInput('api_key', e.target.value)} fullWidth
              type={show.api_key ? 'text' : 'password'}
              InputProps={{ endAdornment: <Eye field="api_key" /> }} />
            <TextField label="API Secret" value={accountDetails.api_secret}
              onChange={e => handleInput('api_secret', e.target.value)} fullWidth
              type={show.api_secret ? 'text' : 'password'}
              InputProps={{ endAdornment: <Eye field="api_secret" /> }} />
            <TextField label="User ID" value={accountDetails.user_id}
              onChange={e => handleInput('user_id', e.target.value)} fullWidth />
            <TextField label="Password" value={accountDetails.password}
              onChange={e => handleInput('password', e.target.value)} fullWidth
              type={show.password ? 'text' : 'password'}
              InputProps={{ endAdornment: <Eye field="password" /> }} />
            <TextField label="TOTP Key" value={accountDetails.totp_key}
              onChange={e => handleInput('totp_key', e.target.value)} fullWidth
              type={show.totp_key ? 'text' : 'password'}
              InputProps={{ endAdornment: <Eye field="totp_key" /> }} />
            <TextField label="Multiplier" value={accountDetails.multiplier}
              onChange={e => handleInput('multiplier', e.target.value)} fullWidth type="number" />
            <FormControlLabel
              control={<Switch checked={!!accountDetails.active} onChange={() => handleInput('active', !accountDetails.active)} />}
              label={<Typography variant="body2">{accountDetails.active ? 'Active' : 'Inactive'}</Typography>}
            />
          </Stack>

          <Button variant="contained" onClick={handleSave} sx={{ mt: 3, px: 4 }}>
            Save Account
          </Button>
          {message && (
            <Typography variant="body2" sx={{ mt: 1.5, color: message.includes('Error') ? 'error.main' : 'success.main' }}>
              {message}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ZerodhaPlaceAccount;
